import React, { useState, useEffect } from 'react';
import {
    trackOtpVerificationView,
    trackOtpVerifyClick,
    trackLoginSuccess,
    trackLoginError,
} from '../../utils/analytics';

const OTPVerification = ({ email, onVerify, onBack }) => {
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        trackOtpVerificationView();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        trackOtpVerifyClick();

        try {
            await onVerify(otp);
            trackLoginSuccess();
        } catch (err) {
            trackLoginError('otp_verify_failed');
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.container}>
            <style>{`
        .otp-box {
          background: var(--card-bg);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          padding: 48px;
          max-width: 480px;
          width: 100%;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
        }
        
        .otp-header {
          text-align: center;
          margin-bottom: 40px;
        }
        
        .otp-logo-section {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }
        
        .otp-logo-image {
          height: 40px;
          width: auto;
        }
        
        .otp-logo-text {
          font-family: "Agrandir Narrow", "Agrandir", Arial, sans-serif;
          font-size: 24px;
          font-weight: 700;
          color: var(--text-primary);
        }
        
        .otp-title {
          font-family: "Agrandir Narrow", "Agrandir", Arial, sans-serif;
          font-size: 38px;
          font-weight: 700;
          font-style: normal;
          color: var(--text-primary);
          margin-bottom: 12px;
        }
        
        .otp-subtitle {
          color: var(--text-secondary);
          font-size: 15px;
          line-height: 1.6;
        }
        
        .otp-input-field {
          text-align: center;
          font-size: 24px;
          letter-spacing: 10px;
        }
        
        .otp-form {
          margin-bottom: 24px;
        }
        
        .otp-resend-text {
          text-align: center;
          color: var(--text-secondary);
          font-size: 14px;
        }
        
        @media (max-width: 768px) {
          .otp-box {
            padding: 32px 24px;
            margin: 0 16px;
            border-radius: 16px;
          }
          
          .otp-header {
            margin-bottom: 32px;
          }
          
          .otp-logo-image {
            height: 32px;
          }
          
          .otp-logo-text {
            font-size: 20px;
          }
          
          .otp-title {
            font-size: 28px;
          }
          
          .otp-subtitle {
            font-size: 14px;
          }
          
          .otp-input-field {
            font-size: 20px;
            letter-spacing: 8px;
          }
        }
        
        @media (max-width: 480px) {
          .otp-box {
            padding: 24px 20px;
            margin: 0 12px;
          }
          
          .otp-logo-section {
            flex-direction: column;
            gap: 8px;
          }
          
          .otp-title {
            font-size: 24px;
          }
          
          .otp-input-field {
            font-size: 18px;
            letter-spacing: 6px;
          }
        }
      `}</style>

            <div className="otp-box">
                <div className="otp-header">
                    <div className="otp-logo-section">
                        <img src="/logo.png" alt="APILoadTest.com" className="otp-logo-image" />
                        <span className="otp-logo-text">APILoadTest.com</span>
                    </div>
                    <h1 className="otp-title">Enter OTP</h1>
                    <p className="otp-subtitle">
                        We've sent a 6-digit code to<br />
                        <strong style={{ color: 'var(--text-primary)' }}>{email}</strong>
                    </p>
                    <p className="otp-subtitle" style={{ marginTop: '10px', fontSize: '13px', opacity: 0.75 }}>
                        📬 Can't find it? Check your <strong>spam or junk folder</strong>.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="otp-form">
                    {error && (
                        <div className="error-message">
                            <span>⚠️</span>
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="input-group">
                        <label htmlFor="otp">One-Time Password</label>
                        <input
                            id="otp"
                            type="text"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="Enter 6-digit OTP"
                            required
                            autoFocus
                            className="otp-input-field"
                        />
                    </div>

                    <button
                        type="submit"
                        className="btn btn-primary"
                        style={{ width: '100%', marginBottom: '10px' }}
                        disabled={loading || otp.length !== 6}
                    >
                        {loading ? 'Verifying...' : 'Verify OTP'}
                    </button>

                    <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ width: '100%' }}
                        onClick={onBack}
                    >
                        Back to Email
                    </button>
                </form>

                <p className="otp-resend-text">
                    Didn't receive the code? Check your spam folder or try again.
                </p>
            </div>
        </div>
    );
};

const styles = {
    container: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        background: 'var(--light-gray)',
    },
};

export default OTPVerification;
